import {
  ApplyPostControlFilterAsync,
  CustomComboBoxMetadata,
  CustomComboBoxUpdateItems,
  HasAggregationAllKeysInItems,
} from "../utils/CustomComboBoxUtil";
import MultiComboBox, {
  MultiComboBox$SelectionChangeEventParameters,
} from "sap/m/MultiComboBox";
import { BetterComboBoxItemFilterFunction } from "../utils/ComboBoxFilterUtil";
import { MetadataOptions } from "sap/ui/base/ManagedObject";
import { debounceAsync } from "../utils/DebounceUtil";

/**
 * @namespace akademika.akamont.customControls
 */
export default class CustomMultiComboBox extends MultiComboBox {
  /**
   * To apply a filter on the component add the `filterURL` and `postControlFilter` paramters to the Control.
   * Note postControlFilter function will only be called if `filterURL` is provided.
   * Note that `postControlFilter=".postControlFilter"` is necessary and sufficient (calls the postControlFilter func from BaseController)
   * Note `postControlFilter` can be bound to a custom func from the controller (remember `filterURL` even if its not needed for the custom func)
   */
  static readonly metadata: MetadataOptions = CustomComboBoxMetadata;

  static renderer = { apiVersion: 2 };

  /**
   * This parameter is necessary to control filter calls to backend in the setSelectedKeys method
   * We don't want the setSelectedKeys method to calls filter on every update, only on those when the keys binding update
   */
  private _selectedKeys: string[] = [];

  init() {
    super.init.apply(this);
    this.setFilterFunction(BetterComboBoxItemFilterFunction);
  }

  updateItems(sReason: string) {
    // @ts-expect-error Method "updateItems" exists, but isn't exposed through the type
    super.updateItems.apply(this, [sReason]);
    CustomComboBoxUpdateItems(this, sReason);
  }

  /**
   * Overriding was necessary to make updates to the list by calling the backend on every key stroke
   * @param oEvent
   */
  public async oninput(oEvent: unknown) {
    const typed = this.getValue();
    await this.debouncedApplyPostControlFilterAsync();

    // @ts-expect-error Method "ComboBox.oninput" exists, but isn't exposed through the type
    super.oninput(oEvent);
    if (this.getValue() !== typed) {
      this.setValue(typed);
      this.selectText(typed.length, typed.length);
    }

    // @ts-expect-error Method "ComboBox.oninput" exists, but isn't exposed through the type
    super.oninput(oEvent);
  }

  // @ts-expect-error Method "ComboBox.fireSelectionChange" exists, but isn't exposed through the type
  public async fireSelectionChange(
    mParameters?: MultiComboBox$SelectionChangeEventParameters
  ) {
    await this.applyPostControlFilterAsync(true);
    super.fireSelectionChange(mParameters);
  }

  /**
   * Overriding this method is necessary so that refresing a page,
   * or navigating to the page makes sure that the filters are also updated
   * @param keys The keys selected in the items list
   */
  // @ts-expect-error Method "MultiComboBox.setSelectedKeys" exists, but isn't exposed through the type
  public async setSelectedKeys(keys: string[]) {
    super.setSelectedKeys(keys);

    const hasItemsLocally = this.getItems().length > 0;
    const missingSelectedItems = !HasAggregationAllKeysInItems(
      this,
      new Set(keys)
    );

    if (missingSelectedItems && !hasItemsLocally) {
      await this.applyPostControlFilterAsync(true);
    }

    this._selectedKeys = keys ? [...keys] : [];
  }

  private debouncedApplyPostControlFilterAsync = debounceAsync(
    this.applyPostControlFilterAsync.bind(this),
    200
  );

  private async applyPostControlFilterAsync(ignoreText = false) {
    await ApplyPostControlFilterAsync(
      this,
      this.getValue(),
      this.getSelectedKeys(),
      ignoreText
    );
  }
}
